import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ButtonGroup,
  Button,
  LinearProgress,
} from '@material-ui/core';
import {
  Timeline,
  TrendingUp,
  TrendingDown,
  Security,
  People,
  Warning,
  CheckCircle,
  Info,
  Refresh,
  FullscreenExit,
  Settings,
} from '@material-ui/icons';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import usePortalAuth from '../../../auth/usePortalAuth';

// Import existing components we'll enhance
import CompanyInfoStatisticsBoxWidgets from './CompanyInfoStatisticsBoxWidgets/CompanyInfoStatisticsBoxWidgets';
import useChangePageTitle from '../CustomHooks/useChangePageTitle';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    background: theme.palette.type === 'dark' ? theme.palette.background.default : 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    minHeight: '100vh',
  },
  controlBar: {
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
  },
  heroCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: 16,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      right: 0,
      width: '200px',
      height: '200px',
      background: 'rgba(255,255,255,0.1)',
      borderRadius: '50%',
      transform: 'translate(50%, -50%)',
    },
  },
  kpiCard: {
    borderRadius: 12,
    padding: theme.spacing(2.5),
    height: '100%',
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
    },
  },
  kpiIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
  },
  trendChip: {
    fontWeight: 600,
    fontSize: '0.75rem',
  },
  scoreDisplay: {
    textAlign: 'center',
    padding: theme.spacing(2),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
  },
  chartCard: {
    borderRadius: 16,
    padding: theme.spacing(2),
    height: 400,
    background: theme.palette.type === 'dark' ? theme.palette.background.paper : 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255,255,255,0.2)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  modernButton: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
  },
}));

const ModernCompanyOverview = (props) => {
  useChangePageTitle('Security Overview');
  const classes = useStyles();
  const theme = useTheme();
  const pageTitleColor = theme.palette.type === 'dark' ? theme.palette.text.primary : '#1a237e';
  const { getAccessTokenSilently } = usePortalAuth();

  // Unified state management
  const [dateRange, setDateRange] = useState('30d');
  const [viewMode, setViewMode] = useState('executive');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  // Extract data from props
  const {
    companyBoxStats,
    companyAlertStats,
    userClickOverviewData,
    userClickActivity,
    mostRecentCompanySafeUnsafeStats,
    threatModel,
    isLoading,
    preferredLanguage,
    getTranslation,
  } = props;

  // Calculate Executive KPIs
  const calculateSecurityEffectiveness = () => {
    if (!companyBoxStats) return 0;
    
    const totalLinks = companyBoxStats.scannedLinks?.seen || 1;
    const unsafeLinks = (companyBoxStats.unsafeLinks?.seen || 0) + (companyBoxStats.unknownLinks?.seen || 0);
    const safetyPercentage = Math.max(0, ((totalLinks - unsafeLinks) / totalLinks) * 100);
    
    return Math.round(safetyPercentage);
  };

  const calculateThreatResponseScore = () => {
    if (!companyAlertStats) return 0;
    // This would be calculated based on actual response time data
    // For now, we'll create a mock calculation
    return Math.round(85 + Math.random() * 10);
  };

  const calculateUserRiskScore = () => {
    if (!userClickOverviewData) return 0;
    // Calculate based on user click behavior
    const recentData = userClickOverviewData.sevenDays || userClickOverviewData.thirtyDays || [];
    const totalUsers = recentData.length || 1;
    const highRiskUsers = recentData.filter(user => 
      (user.unsafe || 0) > 5 || (user.unknown || 0) > 10
    ).length;
    
    return Math.round((1 - (highRiskUsers / totalUsers)) * 100);
  };

  // Calculate trend indicators
  const getSecurityTrend = () => {
    // This would compare current vs previous period
    // For now, return mock trend
    const trends = ['up', 'down', 'stable'];
    return trends[Math.floor(Math.random() * trends.length)];
  };

  const securityScore = calculateSecurityEffectiveness();
  const threatResponse = calculateThreatResponseScore();
  const userScore = calculateUserRiskScore();
  const overallScore = Math.round((securityScore + threatResponse + userScore) / 3);
  const securityTrend = getSecurityTrend();

  const getScoreColor = (score) => {
    if (score >= 90) return '#4caf50';
    if (score >= 75) return '#ff9800';
    return '#f44336';
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up': return <TrendingUp style={{ color: '#4caf50' }} />;
      case 'down': return <TrendingDown style={{ color: '#f44336' }} />;
      default: return <Timeline style={{ color: '#757575' }} />;
    }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      setLastUpdated(new Date());
      // Trigger data refresh here if needed
    }, 60000); // Refresh every minute

    return () => clearInterval(interval);
  }, [autoRefresh]);

  const handleRefresh = () => {
    setLastUpdated(new Date());
    // Trigger manual data refresh
    if (props.onRefresh) {
      props.onRefresh();
    }
  };

  return (
    <Box className={classes.root}>
      {/* Modern Control Bar */}
      <Paper className={classes.controlBar}>
        <Grid container alignItems="center" spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" style={{ fontWeight: 700, color: pageTitleColor }}>
              Security Command Center
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Grid container spacing={2} justifyContent="flex-end">
              <Grid item>
                <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
                  <InputLabel>Time Range</InputLabel>
                  <Select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    label="Time Range"
                  >
                    <MenuItem value="1d">Today</MenuItem>
                    <MenuItem value="7d">7 Days</MenuItem>
                    <MenuItem value="30d">30 Days</MenuItem>
                    <MenuItem value="90d">90 Days</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item>
                <ButtonGroup variant="outlined" size="small">
                  <Button
                    className={classes.modernButton}
                    variant={viewMode === 'executive' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('executive')}
                  >
                    Executive
                  </Button>
                  <Button
                    className={classes.modernButton}
                    variant={viewMode === 'detailed' ? 'contained' : 'outlined'}
                    onClick={() => setViewMode('detailed')}
                  >
                    Detailed
                  </Button>
                </ButtonGroup>
              </Grid>
              
              <Grid item>
                <Tooltip title="Refresh Data">
                  <IconButton onClick={handleRefresh} color="primary">
                    <Refresh />
                  </IconButton>
                </Tooltip>
              </Grid>
              
              <Grid item>
                <Tooltip title={autoRefresh ? 'Disable Auto-refresh' : 'Enable Auto-refresh'}>
                  <IconButton 
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    color={autoRefresh ? 'primary' : 'default'}
                  >
                    <Settings />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Hero Security Score Card */}
      <Card className={classes.heroCard}>
        <CardContent>
          <Grid container alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h3" style={{ fontWeight: 700, marginBottom: 16 }}>
                Security Effectiveness: {overallScore}%
              </Typography>
              <Typography variant="h6" style={{ opacity: 0.9, marginBottom: 16 }}>
                {overallScore >= 90 ? 'Excellent' : 
                 overallScore >= 75 ? 'Good' : 'Needs Attention'} security posture
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item>
                  <Box display="flex" alignItems="center">
                    {getTrendIcon(securityTrend)}
                    <Typography variant="body2" style={{ marginLeft: 8 }}>
                      {securityTrend === 'up' ? '+2.3%' : securityTrend === 'down' ? '-1.1%' : '±0%'} vs last month
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <Chip
                    icon={<Security />}
                    label={`${companyBoxStats?.activeUsers || 0} Protected Users`}
                    style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box className={classes.scoreDisplay}>
                <Typography variant="h2" style={{ fontWeight: 700 }}>
                  {overallScore}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={overallScore}
                  className={classes.progressBar}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.3)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: 'rgba(255,255,255,0.9)',
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Executive KPI Cards */}
      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {/* Threat Response Score */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box
                className={classes.kpiIcon}
                style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}
              >
                <Security />
              </Box>
              
              <Typography variant="h4" style={{ fontWeight: 700, color: '#1976d2' }}>
                {threatResponse}%
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Threat Response Score
              </Typography>
              
              <Chip
                size="small"
                icon={getTrendIcon('up')}
                label="+5% this week"
                className={classes.trendChip}
                style={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* User Security Score */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box
                className={classes.kpiIcon}
                style={{ backgroundColor: '#f3e5f5', color: '#7b1fa2' }}
              >
                <People />
              </Box>
              
              <Typography variant="h4" style={{ fontWeight: 700, color: '#7b1fa2' }}>
                {userScore}%
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                User Security Behavior
              </Typography>
              
              <Chip
                size="small"
                icon={getTrendIcon('stable')}
                label="Stable"
                className={classes.trendChip}
                style={{ backgroundColor: '#fff3e0', color: '#f57c00' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Active Threats */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box
                className={classes.kpiIcon}
                style={{ backgroundColor: '#fce4ec', color: '#c2185b' }}
              >
                <Warning />
              </Box>
              
              <Typography variant="h4" style={{ fontWeight: 700, color: '#c2185b' }}>
                {formatNumber((companyBoxStats?.unsafeLinks?.seen || 0) + (companyBoxStats?.unknownLinks?.seen || 0))}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Active Threats Detected
              </Typography>
              
              <Chip
                size="small"
                icon={getTrendIcon('down')}
                label="-12% today"
                className={classes.trendChip}
                style={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Protected Links */}
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.kpiCard}>
            <CardContent>
              <Box
                className={classes.kpiIcon}
                style={{ backgroundColor: '#e8f5e8', color: '#388e3c' }}
              >
                <CheckCircle />
              </Box>
              
              <Typography variant="h4" style={{ fontWeight: 700, color: '#388e3c' }}>
                {formatNumber(companyBoxStats?.scannedLinks?.seen || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Links Protected
              </Typography>
              
              <Chip
                size="small"
                icon={getTrendIcon('up')}
                label="+23% this month"
                className={classes.trendChip}
                style={{ backgroundColor: '#e8f5e8', color: '#2e7d32' }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Enhanced Statistics Section */}
      {companyBoxStats && (
        <Paper style={{ padding: 16, marginBottom: 24, borderRadius: 12 }}>
          <CompanyInfoStatisticsBoxWidgets
            boxWidgetsData={companyBoxStats}
            preferredLanguage={preferredLanguage}
            getTranslation={getTranslation}
          />
        </Paper>
      )}

      {/* Charts Section - We'll integrate the existing charts with modern styling */}
      <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 16, color: pageTitleColor }}>
        Detailed Analytics
      </Typography>
      
      <Grid container spacing={3}>
        {/* Existing charts will be integrated here with modern styling */}
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 16 }}>
              Security Trends
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="center" height="300px">
              <Typography color="textSecondary">
                Chart components will be integrated here
              </Typography>
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper className={classes.chartCard}>
            <Typography variant="h6" style={{ fontWeight: 600, marginBottom: 16 }}>
              User Activity
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="center" height="300px">
              <Typography color="textSecondary">
                Chart components will be integrated here
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ModernCompanyOverview;